import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  MenuItem,
  Grid,
} from "@mui/material";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import LoaderContainer from "../../components/LoaderContainer";
import CustomAppBar from "../../components/AppBar";
import {
  CustomLabel,
  CustomTitle,
} from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import GenericButton from "../../components/Custom/GenericButton";

export default function CreateProgramForm() {
  const { programId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    name: "",
    module: "IPM",
    program_type: "HYBRID",
    commodity: "",
    states: "",
    farmer_cost: "",
    ghg: "",
    water: "",
    program_end_date: "",
    program_docs: null,
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isProgramCreated, setIsProgramCreated] = useState(false);
  const [createdProgramId, setCreatedProgramId] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [states, setStates] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [commoditiesResponse, statesResponse] = await Promise.all([
          api.get("/common/commodities"),
          api.get("/common/states"),
        ]);
        setCommodities(commoditiesResponse.data);
        setStates(statesResponse.data);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        showAlert("error", "Failed to fetch dropdown data");
      }
    };

    fetchDropdownData();

    if (programId) {
      setIsEditMode(true);
      fetchProgramDetails();
    }
  }, [programId]);

  const fetchProgramDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/programs/programs/${programId}`);
      const programData = response.data;

      if (programData.program_inputs && programData.program_inputs.length > 0) {
        const programInput = programData.program_inputs[0];
        setFormData({
          name: programData.name,
          module: programData.module,
          program_type: programData.program_type,
          commodity: programInput.commodity?.id || "",
          states: programInput.states?.id || "",
          farmer_cost: programInput.farmer_cost || "",
          ghg: programInput.ghg || "",
          water: programInput.water || "",
          program_end_date: programInput.program_end_date || "",
          program_docs: programInput.program_docs || null,
        });
      } else {
        setFormData({
          ...formData,
          name: programData.name,
          program_type: programData.program_type,
        });
      }

      setIsProgramCreated(true);
      setCreatedProgramId(programData.id);
    } catch (error) {
      console.error("Error fetching program details:", error);
      showAlert("error", "Failed to fetch program details");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let response;
      if (isEditMode) {
        response = await api.patch(`/programs/programs/${programId}/`, {
          name: formData.name,
          program_type: formData.program_type,
        });
      } else {
        response = await api.post("/programs/programs/", {
          name: formData.name,
          program_type: formData.program_type,
        });
      }

      if (response && response.data) {
        showAlert(
          "success",
          isEditMode
            ? "Program updated successfully!"
            : "Program created successfully!"
        );
        setIsProgramCreated(true);
        setCreatedProgramId(response.data.id);
        setFormData({
          ...formData,
          name: response.data.name,
        });
      }
    } catch (error) {
      showAlert(
        "error",
        isEditMode
          ? "Error updating the program."
          : "Error creating the program."
      );
      console.error("Program submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProgramInputSubmit = async () => {
    if (!createdProgramId && !isEditMode) {
      showAlert(
        "error",
        "Program ID is missing. Please create a program first."
      );
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSubmit = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSubmit.append(key, formData[key]);
        }
      });
      formDataToSubmit.append("program", createdProgramId || programId);

      let response;
      if (isEditMode) {
        const existingProgramInput = (
          await api.get(`/programs/programs/${programId}`)
        ).data.program_inputs[0];

        if (existingProgramInput) {
          response = await api.patch(
            `/programs/programinputs/${existingProgramInput.id}/`,
            formDataToSubmit
          );
        } else {
          response = await api.post(
            "/programs/programinputs/",
            formDataToSubmit
          );
        }
      } else {
        response = await api.post("/programs/programinputs/", formDataToSubmit);
      }

      if (response && response.data) {
        showAlert(
          "success",
          isEditMode
            ? "Program input details updated successfully!"
            : "Program input details added successfully!"
        );
        navigate(`/programs/${response.data.id}/add-tasks`, {
          state: {
            program_type: formData.program_type,
            program_id: response.data.program,
          },
        });
      }
    } catch (error) {
      showAlert(
        "error",
        isEditMode
          ? "Error updating program input details."
          : "Error adding program input details."
      );
      console.error("Program input submission error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs">
          <CustomTitle>
            {isEditMode ? t("Edit Program") : t("Create Programs")}
          </CustomTitle>
          <Box mt={2}>
            <CustomLabel>{t("Enter Program Name")}</CustomLabel>
            <Box mt={2}>
              <TextField
                label={t("Program Name")}
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
                disabled={isProgramCreated}
              />
            </Box>
          </Box>
          <Box mt={2}>
            <CustomLabel>{t("Select Program Type")}</CustomLabel>
            <Box mt={2}>
              <TextField
                select
                label={t("Module")}
                name="module"
                value={formData.module}
                onChange={handleChange}
                fullWidth
                disabled={isProgramCreated}
              >
                <MenuItem value="IPM">{t("IPM")}</MenuItem>
                <MenuItem value="DECARB">{t("Decarbonization")}</MenuItem>
                <MenuItem value="YE">{t("Yield Enhancement")}</MenuItem>
              </TextField>
            </Box>
          </Box>
          <Box mt={2}>
            <CustomLabel>{t("Select Program Type")}</CustomLabel>
            <Box mt={2}>
              <TextField
                select
                label={t("Program Type")}
                name="program_type"
                value={formData.program_type}
                onChange={handleChange}
                fullWidth
                disabled={isProgramCreated}
              >
                <MenuItem value="HYBRID">{t("Hybrid")}</MenuItem>
                <MenuItem value="VERIFIABLE">{t("Verifiable")}</MenuItem>
                <MenuItem value="VOLUNTARY">{t("Voluntary")}</MenuItem>
              </TextField>
            </Box>
          </Box>
          <Box mt={2}>
            <GenericButton
              onClick={handleSubmit}
              backgroundColor="#4CAF50"
              fontColor="white"
              hideIcon
              disabled={submitting || isProgramCreated}
              padding="8px 0"
            >
              {submitting
                ? t("Submitting...")
                : isEditMode
                ? t("Update Program")
                : t("Create Program")}
            </GenericButton>
          </Box>

          <Box mt={2}>
            <CustomLabel>{t("Program Details")}</CustomLabel>
            <Box mt={2}>
              <TextField
                select
                label={t("Crop applicable to")}
                name="commodity"
                value={formData.commodity}
                onChange={handleChange}
                fullWidth
                disabled={!isProgramCreated}
              >
                {commodities.map((commodity) => (
                  <MenuItem key={commodity.id} value={commodity.id}>
                    {commodity.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          <Box mt={2}>
            <TextField
              select
              label={t("Geographies applicable to")}
              name="states"
              value={formData.states}
              onChange={handleChange}
              fullWidth
              disabled={!isProgramCreated}
            >
              {states.map((state) => (
                <MenuItem key={state.id} value={state.id}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box mt={2}>
            <TextField
              label={t("Cost to farmer per acre (provisional)")}
              name="farmer_cost"
              value={formData.farmer_cost}
              onChange={handleChange}
              fullWidth
              type="number"
              disabled={!isProgramCreated}
            />
          </Box>
          <Box mt={2}>
            <CustomLabel>{t("Impact estimate per acre")}</CustomLabel>
            <Grid container spacing={1} mt={1}>
              <Grid item xs={6}>
                <TextField
                  label={t("GHG")}
                  name="ghg"
                  value={formData.ghg}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  disabled={!isProgramCreated}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t("Water")}
                  name="water"
                  value={formData.water}
                  onChange={handleChange}
                  fullWidth
                  type="number"
                  disabled={!isProgramCreated}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={2}>
            <TextField
              label={t("Program end date (provisional)")}
              name="program_end_date"
              value={formData.program_end_date}
              onChange={handleChange}
              fullWidth
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!isProgramCreated}
            />
          </Box>
          <Box mt={2}>
            <input
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              name="program_docs"
              onChange={handleChange}
              id="program-docs"
            />
            <Button
              variant="contained"
              component="label"
              fullWidth
              disabled={!isProgramCreated}
              sx={{ color: "white", padding: "8px 0" }}
              htmlFor="program-docs"
            >
              {t("Program Docs")}
            </Button>
          </Box>
          <Box mt={2}>
            <GenericButton
              onClick={handleProgramInputSubmit}
              disabled={!isProgramCreated || submitting}
              padding="8px 0"
              fontSize="14px"
              hideIcon
              backgroundColor="#4CAF50"
              fontColor="white"
            >
              {submitting ? t("Submitting...") : t("Submit Program Input")}
            </GenericButton>
          </Box>
        </Container>
      </LoaderContainer>
    </>
  );
}
